import { pb } from "@/lib/pocketbase";
import { z } from "zod";
import type { Product, Price } from "@/types/pocketbase-types";

// Zod schema for PlanId validation
const planIdSchema = z.enum(["intern", "junior", "senior"] as const);

// Validation schema for billing address
const billingAddressSchema = z.object({
  country: z.string().length(2, "Country must be ISO alpha-2 code"),
  street: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  zipcode: z.string().min(1, "Zip code is required"),
  state: z.string().min(1, "State is required"),
});

// Validation schema for payment requests
export const createSubscriptionSchema = z.object({
  planId: z.string().min(1, "Plan ID is required"), // Accept any string for Stripe price IDs
  priceId: z.string().min(1, "Price ID is required"), // Accept any string for Stripe price IDs
  billingType: z.enum(["monthly", "annual"], {
    errorMap: () => ({ message: "Please select monthly or annual billing" }),
  }),
  customerEmail: z
    .string()
    .email("Please enter a valid email address")
    .min(1, "Email is required"),
  customerName: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  orgId: z.string().min(1, "Organization ID is required"),
  redirectUrl: z
    .string()
    .url("Please enter a valid URL")
    .optional()
    .or(z.literal("")),
  cancelUrl: z
    .string()
    .url("Please enter a valid URL")
    .optional()
    .or(z.literal("")),
  state: z.string().optional(),
  billingAddress: billingAddressSchema.optional(),
  isTrial: z.boolean().optional(),
});

export type CreateSubscriptionRequest = z.infer<
  typeof createSubscriptionSchema
>;

export interface CreateSubscriptionResponse {
  paymentUrl: string;
  subscriptionId: string;
}

export const createSubscription = async (
  request: CreateSubscriptionRequest,
): Promise<CreateSubscriptionResponse> => {
  // Validate the request data
  const validatedRequest = createSubscriptionSchema.parse(request);

  const response = await pb.send("/api/v1/billing/create-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(validatedRequest),
  });

  return response;
};

// Validation schema for change plan requests
export const changePlanSchema = z.object({
  subscriptionId: z.string().min(1, "Subscription ID is required"),
  newProductId: z.string().min(1, "New product ID is required"),
  planId: planIdSchema.optional(),
  billingType: z.enum(["monthly", "annual"]).optional(),
});

export type ChangePlanRequest = z.infer<typeof changePlanSchema>;

export const changePlan = async (request: ChangePlanRequest) => {
  // Validate the request data
  const validatedRequest = changePlanSchema.parse(request);

  const response = await pb.send("/api/v1/billing/change-plan", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(validatedRequest),
  });

  return response;
};

// Validation schema for cancel subscription requests
export const cancelSubscriptionSchema = z.object({
  subscriptionId: z.string().min(1, "Subscription ID is required"),
  reasons: z
    .array(z.string())
    .min(1, "At least one cancellation reason is required"),
  expectationsMet: z.enum(["not_at_all", "somewhat", "fully"], {
    errorMap: () => ({
      message: "Please select how well the product met your expectations",
    }),
  }),
  improvementFeedback: z.string().optional(),
});

export type CancelSubscriptionRequest = z.infer<
  typeof cancelSubscriptionSchema
>;

export interface CancelSubscriptionResponse {
  status: string;
  message: string;
  cancellation_date?: string;
  cancel_at_period_end?: boolean;
}

export const cancelSubscription = async (
  request: CancelSubscriptionRequest,
): Promise<CancelSubscriptionResponse> => {
  // Validate the request data
  const validatedRequest = cancelSubscriptionSchema.parse(request);

  const response = await pb.send("/api/v1/billing/cancel-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(validatedRequest),
  });

  return response;
};

// Types for product and price data (using PocketBase types)
export type StripeProduct = Product & {
  metadata: Record<string, string>;
  expand?: {
    price_via_product?: Price[];
  };
};

export type StripePrice = Price & {
  metadata: Record<string, string>;
};

export interface ProductPriceResponse {
  products: StripeProduct[];
  prices: StripePrice[];
}

// Legacy function - kept for backward compatibility but now unused
// Use getActiveProductsAndPrices() instead which uses PocketBase client SDK
export const getProductIds = async (): Promise<
  Record<string, string> | ProductPriceResponse
> => {
  console.warn(
    "getProductIds is deprecated. Use getActiveProductsAndPrices() instead.",
  );
  const response = await pb.send("/api/v1/billing/product-ids", {
    method: "GET",
  });

  return response;
};

// Helper function to get active products and prices using PocketBase client SDK
export const getActiveProductsAndPrices =
  async (): Promise<ProductPriceResponse> => {
    try {
      // Fetch active products with expanded prices using PocketBase client SDK
      const products = await pb
        .collection("product")
        .getList<StripeProduct>(1, 50, {
          filter: "active = true",
          sort: "-product_order",
          expand: "price_via_product",
        });

      // Extract prices from expanded data
      const allPrices: StripePrice[] = [];
      const productsWithMetadata: StripeProduct[] = products.items.map(
        (product) => {
          // Ensure metadata is properly typed
          const metadata =
            typeof product.metadata === "object" && product.metadata !== null
              ? (product.metadata as Record<string, string>)
              : {};

          // Extract prices from expand and add to allPrices array
          if (product.expand?.price_via_product) {
            const productPrices = product.expand.price_via_product.map(
              (price) => ({
                ...price,
                metadata:
                  typeof price.metadata === "object" && price.metadata !== null
                    ? (price.metadata as Record<string, string>)
                    : {},
              }),
            );
            allPrices.push(...productPrices);
          }

          return {
            ...product,
            metadata,
          };
        },
      );

      return {
        products: productsWithMetadata,
        prices: allPrices,
      };
    } catch (error) {
      console.error(
        "Failed to fetch products and prices from PocketBase:",
        error,
      );
      // Return empty arrays on error
      return {
        products: [],
        prices: [],
      };
    }
  };
